import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import * as ErrorReporting from 'services/error-reporting/error-reporting'
import { Button, Dialog, DialogContent, DialogTitle, Heading, Text } from 'components'
import { useAnalytics } from 'hooks'
import { useIsMobile } from 'hooks/use-mobile'
import { Calendar, ChevronUp, Settings2, Sparkles, Zap } from 'lucide-react'
import { selectBusinessId } from 'modules/authentication'
import { useAppSelector } from 'modules/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { extractApiError } from '../../../creative/utils/extract-api-error'
import { PostModal } from '../../../creative/components/PostModal'
import { useIdeaStore, type PostIdea } from '../../../creative/store/idea-store'
import { useConnectedPlatforms } from '../../hooks/useConnectedPlatforms'
import { useAutopilotStore } from '../../store/autopilot-store'
import {
  selectItemsForDay,
  selectMissingPlatformsThisWeek,
  useWeeklyQueueStore,
  type WeeklyQueueItem,
} from '../../store/weekly-queue-store'
import { dayOffsetUtc, type ConnectableChannel } from '../../utils/weekly-queue-utils'
import { DefaultsPopover } from '../DefaultsPopover/DefaultsPopover'
import {
  ConnectPlatformsModal,
  type ConnectPromptTrigger,
  type ModalPlatform,
} from './ConnectPlatformsModal'
import { DayDetail } from './DayDetail'
import { WeekGrid } from './WeekGrid'
import { WeekGridGhost } from './WeekGridGhost'
import { WeeklyPlanWizard } from './WeeklyPlanWizard'
import { WeekStatusHero } from './WeekStatusHeader'
import { WeekStrip } from './WeekStrip'

function countByStatus(items: WeeklyQueueItem[], status: WeeklyQueueItem['status']) {
  return items.filter(it => it.status === status).length
}

export function WeeklyCockpit({ onCollapse }: { onCollapse?: () => void } = {}) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { track } = useAnalytics()
  const isMobile = useIsMobile()
  const [defaultsOpen, setDefaultsOpen] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [editPlanOpen, setEditPlanOpen] = useState(false)
  const fetchAutopilots = useAutopilotStore(s => s.fetchAutopilots)
  const autopilotLoaded = useAutopilotStore(s => s.loaded)
  const autopilotLoadFailed = useAutopilotStore(s => s.loadFailed)
  const activePlan = useAutopilotStore(s => s.autopilots[0])
  const isPlanPaused = activePlan?.status === 'paused'
  const businessId = useAppSelector(selectBusinessId)
  const {
    loading: connectedPlatformsLoading,
    connectedProviders,
    refetch: refetchConnectedPlatforms,
  } = useConnectedPlatforms()

  const items = useWeeklyQueueStore(s => s.items)
  const days = useWeeklyQueueStore(s => s.days)
  const selectedDate = useWeeklyQueueStore(s => s.selectedDate)
  const loaded = useWeeklyQueueStore(s => s.loaded)
  const loading = useWeeklyQueueStore(s => s.loading)
  const hasActivePlan = useWeeklyQueueStore(s => s.hasActivePlan)
  const scheduledCount = useWeeklyQueueStore(s => s.scheduledCount)
  const generatingCount = useWeeklyQueueStore(s => s.generatingCount)
  const publishedCount = useWeeklyQueueStore(s => s.publishedCount)
  const pendingApprovalCount = useWeeklyQueueStore(s => s.pendingApprovalCount)
  const publishMode = useWeeklyQueueStore(s => s.publishMode)
  const approvePost = useWeeklyQueueStore(s => s.approvePost)
  const windowStart = useWeeklyQueueStore(s => s.windowStart)
  const pendingActions = useWeeklyQueueStore(s => s.pendingActions)
  const optimisticDrafts = useWeeklyQueueStore(s => s.optimisticDrafts)
  const justGeneratedIds = useWeeklyQueueStore(s => s.justGeneratedIds)
  const sessionActions = useWeeklyQueueStore(s => s.sessionActions)
  const sessionStartedAt = useWeeklyQueueStore(s => s.sessionStartedAt)
  const fetchQueue = useWeeklyQueueStore(s => s.fetchQueue)
  const selectDay = useWeeklyQueueStore(s => s.selectDay)
  const deletePost = useWeeklyQueueStore(s => s.deletePost)
  const unschedule = useWeeklyQueueStore(s => s.unschedule)
  const markSessionStart = useWeeklyQueueStore(s => s.markSessionStart)
  const resetSession = useWeeklyQueueStore(s => s.resetSession)
  const cancelPolling = useWeeklyQueueStore(s => s.cancelPolling)
  const addPostForDay = useWeeklyQueueStore(s => s.addPostForDay)
  const loadIdea = useIdeaStore(s => s.loadIdea)

  // PostModal hub — clicking a card loads the post's project into a PostIdea
  // and opens the same schedule/approve/view-live modal Home uses. `openingId`
  // drives a brief per-card loading veil during the loadIdea round-trip.
  const [activeIdea, setActiveIdea] = useState<PostIdea | null>(null)
  const [openingId, setOpeningId] = useState<string | null>(null)

  const total = items.length
  const completionTrackedRef = useRef(false)
  const viewedTrackedRef = useRef(false)
  const initialDaySelectionTrackedRef = useRef<string | null>(null)

  useEffect(() => {
    fetchQueue()
    markSessionStart()
    return () => cancelPolling()
  }, [fetchQueue, markSessionStart, cancelPolling])

  // Keep the active plan loaded so "Edit plan" (stepper) and the manage popover
  // have the plan to pre-fill / act on without a click-time fetch race.
  useEffect(() => {
    if (!autopilotLoaded) void fetchAutopilots()
  }, [autopilotLoaded, fetchAutopilots])

  const handleOpenEditPlan = useCallback(() => {
    if (!autopilotLoaded) void fetchAutopilots()
    setEditPlanOpen(true)
  }, [autopilotLoaded, fetchAutopilots])

  const handleEditPlanDone = useCallback(() => {
    setEditPlanOpen(false)
    void fetchQueue()
  }, [fetchQueue])

  // Other surfaces (the calendar's PlanPitchBanner, WeeklyComplete, deprecated
  // /autopilot/setup) navigate here with `?plan=open` to pop the summary
  // popover, or `?plan=edit` to jump straight into the plan editor. Wait for
  // the plan fetch and route by what actually EXISTS: with no plan yet, both
  // targets are dead ends (a summary/editor for a non-existent plan renders
  // nothing) — open the setup wizard instead, which is what a no-plan user
  // was promised by the CTA that sent them here. Strip the param once routed
  // (not while the fetch is in flight) so refresh / back doesn't re-open it.
  useEffect(() => {
    const planParam = searchParams.get('plan')
    if (planParam !== 'open' && planParam !== 'edit') return
    if (!autopilotLoaded) {
      void fetchAutopilots()
      return
    }
    // A FAILED fetch reports "no plan" for users who may well have one — opening the
    // create wizard on that lie risks a duplicate plan. Fall back to the old popover.
    if (!activePlan && !autopilotLoadFailed) setWizardOpen(true)
    else if (planParam === 'edit' && activePlan) setEditPlanOpen(true)
    else setDefaultsOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete('plan')
    setSearchParams(next, { replace: true })
  }, [
    searchParams,
    setSearchParams,
    fetchAutopilots,
    autopilotLoaded,
    autopilotLoadFailed,
    activePlan,
  ])

  const handleDefaultsOpenChange = useCallback(
    (open: boolean) => {
      setDefaultsOpen(open)
      // When closing, refresh the queue: a defaults change may have triggered
      // a new generation downstream that we want to reflect immediately.
      if (!open) void fetchQueue()
    },
    [fetchQueue]
  )

  // Plan creation now happens through the explicit weekly-plan wizard
  // (WeeklyPlanWizard) rendered in the empty state below — a plan publishes on
  // the user's behalf, so we no longer spin one up silently. `onCreated`
  // refreshes the queue so the cockpit leaves the empty state once the kickoff
  // generation lands.
  const handlePlanCreated = useCallback(() => {
    setWizardOpen(false)
    // Refresh both stores: the queue leaves the empty state once kickoff
    // generation lands, and the autopilot store now carries the plan so the
    // cockpit header swaps its "Set up my weekly plan" CTA for the plan popover.
    void fetchAutopilots()
    void fetchQueue()
  }, [fetchAutopilots, fetchQueue])

  // Hold the view event until we also know the connection state so we can
  // emit `missing_platforms` in the same event. Without it the connect-prompt
  // funnel can only be reconstructed via a separate join.
  useEffect(() => {
    if (!loaded || connectedPlatformsLoading || viewedTrackedRef.current) return
    viewedTrackedRef.current = true
    const missing = selectMissingPlatformsThisWeek({ items }, connectedProviders)
    track(AnalyticsEvents.Autopilot.WEEKLY_QUEUE_VIEWED, {
      scheduled_count: scheduledCount,
      generating_count: generatingCount,
      published_count: publishedCount,
      total_items: total,
      has_active_plan: hasActivePlan,
      platform: isMobile ? 'mobile' : 'desktop',
      business_id: businessId ?? undefined,
      missing_platforms_count: missing.length,
      missing_platforms: missing.map(p => p.channel),
    })
  }, [
    loaded,
    connectedPlatformsLoading,
    scheduledCount,
    generatingCount,
    publishedCount,
    total,
    hasActivePlan,
    isMobile,
    businessId,
    items,
    connectedProviders,
    track,
  ])

  useEffect(() => {
    if (!loaded) return
    if (total === 0) return
    // The week is "settled" once nothing is still drafting.
    if (generatingCount > 0) {
      completionTrackedRef.current = false
      return
    }
    if (completionTrackedRef.current) return
    if (sessionActions === 0) return
    completionTrackedRef.current = true
    track(AnalyticsEvents.Autopilot.WEEKLY_SESSION_COMPLETED, {
      total_actions: sessionActions,
      duration_ms: sessionStartedAt ? Date.now() - sessionStartedAt : undefined,
    })
    resetSession()
  }, [loaded, total, generatingCount, sessionActions, sessionStartedAt, track, resetSession])

  const handleSelectDay = useCallback(
    (date: string, source: 'tap' | 'keyboard') => {
      selectDay(date)
      const day = days.find(d => d.date === date)
      const ids = day ? new Set(day.itemIds) : null
      const dayItems = ids ? items.filter(item => ids.has(item.creativeId)) : []
      const offset = dayOffsetUtc(date, windowStart) ?? 0
      track(AnalyticsEvents.Autopilot.WEEKLY_DAY_SELECTED, {
        date,
        day_offset: offset,
        pending_count: countByStatus(dayItems, 'scheduled') + countByStatus(dayItems, 'generating'),
        approved_count: countByStatus(dayItems, 'published'),
        source,
      })
    },
    [selectDay, items, days, windowStart, track]
  )

  // Track the initial day selection (when load + auto-select happens).
  useEffect(() => {
    if (!loaded || !selectedDate) return
    if (initialDaySelectionTrackedRef.current === selectedDate) return
    initialDaySelectionTrackedRef.current = selectedDate
    const dayItems = selectItemsForDay(useWeeklyQueueStore.getState(), selectedDate)
    const offset = dayOffsetUtc(selectedDate, windowStart) ?? 0
    track(AnalyticsEvents.Autopilot.WEEKLY_DAY_SELECTED, {
      date: selectedDate,
      day_offset: offset,
      pending_count: countByStatus(dayItems, 'scheduled') + countByStatus(dayItems, 'generating'),
      approved_count: countByStatus(dayItems, 'published'),
      source: 'initial',
    })
  }, [loaded, selectedDate, windowStart, track])

  // Platforms that have at least one upcoming scheduled post but no OAuth
  // connection — drives both the cockpit's connect-platforms warning row and
  // the multi-platform connect modal opened from it.
  const missingPlatforms = useMemo<ModalPlatform[]>(() => {
    if (connectedPlatformsLoading) return []
    return selectMissingPlatformsThisWeek({ items }, connectedProviders)
  }, [items, connectedProviders, connectedPlatformsLoading])

  const [connectModalState, setConnectModalState] = useState<{
    trigger: ConnectPromptTrigger
    creativeId?: string
    platforms: ModalPlatform[]
  } | null>(null)

  const handleConnectModalOpenChange = useCallback((open: boolean) => {
    if (!open) setConnectModalState(null)
  }, [])

  // Header CTA — open the multi-platform connect modal for every platform that
  // has a scheduled/generating post but no OAuth connection. This is the
  // surviving connect-prompt trigger now that per-post approval (and its
  // `post_approval` trigger) is gone.
  const handleOpenConnectModal = useCallback(() => {
    if (missingPlatforms.length === 0) return
    setConnectModalState({ trigger: 'header_cta', platforms: missingPlatforms })
  }, [missingPlatforms])

  const handleConnectModalConnected = useCallback(
    (_channel: ConnectableChannel) => {
      void refetchConnectedPlatforms()
    },
    [refetchConnectedPlatforms]
  )

  const handleDelete = useCallback(
    async (creativeId: string, channel: string) => {
      // Hard delete — removes the whole Post (project + siblings) and tombstones
      // the autopilot slot so it isn't refilled. No Undo: callers gate this
      // behind a "Delete this post?" confirm dialog.
      try {
        await deletePost(creativeId)
        // The delete itself is tracked server-side at the single chokepoint
        // (ProjectService.delete → "Post Deleted"), which knows the post's
        // pre-delete state, platforms, and whether a slot was tombstoned.
        toast('Post deleted', {
          duration: 5_000,
          description: 'It won’t publish, and the slot won’t be refilled.',
        })
      } catch (error) {
        ErrorReporting.captureException(error, {
          tags: { feature: 'weekly_cockpit', step: 'delete', channel },
          extra: { creativeId, businessId },
        })
        console.error('Delete failed:', error)
        toast.error('Couldn’t delete the post.')
      }
    },
    [deletePost, track, businessId]
  )

  // Whole-card click → open PostModal. Load the post's project into a PostIdea
  // (loadIdea hydrates the idea + creative stores PostModal reads from), then
  // surface the modal. Legacy creatives with no projectId fall back to the
  // composer (they can't be represented as a PostIdea).
  const handleOpenPost = useCallback(
    async (creativeId: string) => {
      const target = items.find(it => it.creativeId === creativeId)
      if (!target?.projectId) {
        navigate(`/compose/${creativeId}`, {
          state: { source: 'weekly_cockpit', entry_source: 'cockpit' },
        })
        return
      }
      setOpeningId(creativeId)
      try {
        const idea = await loadIdea(target.projectId)
        if (idea) setActiveIdea(idea)
        else toast.error('Couldn’t open this post. Please try again.')
      } catch (err) {
        ErrorReporting.captureException(err, {
          tags: { feature: 'weekly_cockpit', step: 'open_post' },
          extra: { creativeId, projectId: target.projectId },
        })
        toast.error('Couldn’t open this post. Please try again.')
      } finally {
        setOpeningId(null)
      }
    },
    [items, loadIdea, navigate]
  )

  // ••• Edit → the post-aware Composer (the Studio). Mirrors PostModal's own
  // "Edit in {platform}" route so editing is consistent across surfaces.
  const handleTweak = useCallback(
    (creativeId: string, channel: string) => {
      track(AnalyticsEvents.Autopilot.WEEKLY_POST_TWEAKED, {
        creative_id: creativeId,
        channel,
      })
      const target = items.find(it => it.creativeId === creativeId)
      // Every creative now belongs to a Project — open the composer in
      // post mode (projectId + primary platform). Legacy `/compose/:creativeId`
      // hits still resolve via ComposerPage's auto-promote effect, but new
      // navigation always goes straight to the project URL.
      const platform = target?.primaryPlatform ?? channel
      if (target?.projectId && platform) {
        navigate(`/compose/${target.projectId}/${platform}`, {
          state: { source: 'weekly_cockpit', entry_source: 'cockpit' },
        })
        return
      }
      navigate(`/compose/${creativeId}`, {
        state: { source: 'weekly_cockpit', entry_source: 'cockpit' },
      })
    },
    [navigate, track, items]
  )

  const handleApprovePost = useCallback(
    async (creativeId: string) => {
      const item = items.find(it => it.creativeId === creativeId)
      try {
        await approvePost(creativeId)
        track(AnalyticsEvents.Posts.POST_APPROVED, {
          surface: 'cockpit',
          creative_id: creativeId,
          project_id: item?.projectId,
          autopilot_id: item?.autopilotId ?? undefined,
        })
        toast.success('Approved. It’ll publish at its scheduled time.')
      } catch (err) {
        // The cockpit is the primary approve surface, so it has to relay the
        // server's reason. Approving validates every platform the stamp
        // releases, and a content refusal names the platform and the fix — a
        // flat "Try again" is worse than useless there, since every retry fails
        // identically until the copy is edited.
        ErrorReporting.captureException(err)
        toast.error(extractApiError(err, 'Couldn’t approve. Try again.'))
      }
    },
    [items, approvePost, track]
  )

  // ••• Unschedule → clear the schedule so the post drops off the week and
  // survives as a draft under /posts. The POST_UNSCHEDULED event is emitted
  // server-side on the shared endpoint. Reversible, so no confirm dialog.
  const handleUnschedule = useCallback(
    async (creativeId: string) => {
      try {
        await unschedule(creativeId)
        // Keep the home/Posts grid in sync — clears the IdeaCard's "Scheduled"
        // chip without waiting for a refetch (no-op if that idea isn't loaded).
        useIdeaStore.getState().markUnscheduled(creativeId)
        toast.success('Moved to draft. Find it in Posts.')
      } catch (err) {
        ErrorReporting.captureException(err, {
          tags: { feature: 'weekly_cockpit', step: 'unschedule' },
          extra: { creativeId, businessId },
        })
        console.error('Unschedule failed:', err)
        toast.error('Couldn’t move this post to draft. Please try again.')
      }
    },
    [unschedule, businessId]
  )

  const selectedItems = useMemo(() => {
    if (!selectedDate) return []
    return selectItemsForDay({ items, days }, selectedDate)
  }, [items, days, selectedDate])

  const selectedExpectedCount = useMemo(() => {
    if (!selectedDate) return 0
    return days.find(d => d.date === selectedDate)?.expectedCount ?? 0
  }, [days, selectedDate])

  // The plan's selected platforms (e.g. ['pinterest', 'facebook']). Mirrors the
  // wizard's `initialChannels` resolution. Used to preview the full publish set
  // on still-generating cards, whose per-post confirmedPlatforms isn't stamped
  // until the autopilot fans the slot out.
  const planChannels = useMemo<string[]>(() => {
    const raw = activePlan?.contentSpecifics?.channels
    if (Array.isArray(raw) && raw.length > 0) return raw as string[]
    return activePlan?.channel ? [activePlan.channel] : []
  }, [activePlan])

  if (loading && !loaded) {
    return (
      <div className="card relative p-5">
        {/* The skeleton is a full render branch too: without this, expanding
            during the initial fetch leaves the user with no way back until
            the queue resolves. */}
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Collapse week"
            aria-expanded="true"
            title="Collapse"
            className="absolute top-3 right-3 z-10 rounded-[8px] p-1.5 text-paper-faint transition-colors hover:bg-bg-3 hover:text-paper"
          >
            <ChevronUp size={16} />
          </button>
        )}
        <div className="mb-4 flex items-center gap-3">
          <div className="h-14 w-14 animate-pulse rounded-full bg-bg-3" />
          <div className="space-y-2">
            <div className="h-4 w-48 animate-pulse rounded bg-bg-3" />
            <div className="h-3 w-32 animate-pulse rounded bg-bg-3" />
          </div>
        </div>
        <div className="mb-4 flex gap-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-[88px] w-[72px] animate-pulse rounded-[10px] bg-bg-3" />
          ))}
        </div>
        <div className="h-32 animate-pulse rounded-[10px] bg-bg-3" />
      </div>
    )
  }

  // Setup empty-state is only for businesses with NO plan AND no scheduled
  // posts. A *paused* plan still exists (it just isn't "active"), so the
  // server's `hasActivePlan` is false for it — guarding on that alone would
  // wrongly drop a paused user back into onboarding; require the autopilot
  // store to confirm there's no plan. And if there are posts scheduled for the
  // week (e.g. orphaned posts from a since-removed plan), we show the cockpit
  // instead so the user can manage them — with a "Set up my weekly plan" CTA in
  // the header (see below) standing in for the plan popover.
  if (loaded && total === 0 && !hasActivePlan && autopilotLoaded && !activePlan) {
    return (
      <div className="card relative overflow-hidden p-6 text-center sm:p-8">
        {/* Focal-screen decoration: subtle grid + acid glow. The three
            cockpit states (empty / loaded / complete) share this register. */}
        <div className="grid-bg pointer-events-none absolute inset-0" aria-hidden />

        {/* Preview the payoff: a blurred mock of the weekly calendar a live
            plan produces, filling the card so the empty state shows what users
            get instead of a bare CTA. Decorative only — hidden while the wizard
            is open so it doesn't fight the form. */}
        {!wizardOpen && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            <WeekGridGhost className="opacity-60 blur-[2.5px]" />
          </div>
        )}

        {/* Center scrim keeps the headline/CTA crisp over the blurred grid while
            the day headers, side columns and tile edges stay visible. Translucent
            (color-mix) rather than solid so the calendar still reads through it. */}
        {!wizardOpen && (
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 60% 52% at 50% 46%, color-mix(in srgb, var(--bg-2) 92%, transparent) 35%, transparent 78%)',
            }}
            aria-hidden
          />
        )}

        <div
          className="glow pointer-events-none absolute"
          style={{
            top: '-120px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '500px',
            height: '300px',
          }}
          aria-hidden
        />
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Collapse week"
            aria-expanded="true"
            title="Collapse"
            className="absolute top-3 right-3 z-20 rounded-[8px] p-1.5 text-paper-faint transition-colors hover:bg-bg-3 hover:text-paper"
          >
            <ChevronUp size={16} />
          </button>
        )}
        <div className="relative z-10">
          <span className="mono mb-3 block text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Sunday Studio
          </span>
          {wizardOpen && businessId ? (
            <WeeklyPlanWizard
              businessId={businessId}
              onCancel={() => setWizardOpen(false)}
              onCreated={handlePlanCreated}
            />
          ) : (
            <>
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-acid">
                <Zap size={26} />
              </div>
              <Heading level="h1">Turn on your weekly plan</Heading>
              <Text variant="body" tone="paper-dim" as="p" className="mx-auto mt-2 max-w-md">
                Taka drafts, schedules, and publishes your posts every week automatically. Tell it
                what to post, how often, and where. Tweak or pause anytime.
              </Text>
              <div className="mt-5 inline-flex">
                <Button variant="t3-primary" size="t3-md" onClick={() => setWizardOpen(true)}>
                  <Sparkles size={16} />
                  Set up my weekly plan
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  // An active plan with an empty week and nothing expected is NOT a "drafting"
  // moment — a freshly-created plan always reports expectedCount > 0 on its
  // cadence days (the grid renders per-slot "Drafting…" placeholders for those).
  // The only way to reach total===0 with every day expecting nothing is a
  // deliberately-emptied week: the user deleted everything and those slots are
  // tombstoned so they won't refill. In that case fall through to the normal
  // cockpit with empty day cells; WeekStatusHero swaps to the "calendar is
  // empty this week" copy and each day offers an Add-post affordance.

  const offset = dayOffsetUtc(selectedDate, windowStart)

  return (
    <div className="flex flex-col gap-4">
      <section className="card relative overflow-hidden p-4 sm:p-5">
        {/* Focal-screen decoration: subtle grid behind every interactive
            surface, soft acid halo above-center to give the daily cockpit
            a sense of arrival. Same pattern as the empty-state and
            WeeklyComplete cards so all three states feel like one room. */}
        <div className="grid-bg pointer-events-none absolute inset-0" aria-hidden />
        <div
          className="glow pointer-events-none absolute"
          style={{
            top: '-96px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '640px',
            height: '300px',
          }}
          aria-hidden
        />
        {/* Content layer: positioned so it stacks above the absolute
          decorations (otherwise the glow would paint over the header). */}
        <div className="relative">
          <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <WeekStatusHero
              toReviewCount={pendingApprovalCount}
              scheduledCount={scheduledCount + generatingCount}
              decidedCount={publishedCount}
              hasMissingPlatforms={missingPlatforms.length > 0}
              isPaused={isPlanPaused}
              publishMode={publishMode}
            />
            <div className="flex items-center gap-2">
              {missingPlatforms.length > 0 && (
                <Button
                  variant="t3-primary"
                  size="t3-sm"
                  title="Connect accounts so your scheduled posts can publish"
                  onClick={handleOpenConnectModal}
                >
                  <Zap size={14} />
                  <span className="hidden sm:inline">Connect accounts</span>
                </Button>
              )}
              {autopilotLoaded && !activePlan ? (
                // Posts are scheduled (orphaned from a removed plan) but there's
                // no plan to manage — offer plan setup instead of the popover.
                <Button
                  variant="t3-primary"
                  size="t3-sm"
                  onClick={() => setWizardOpen(true)}
                  title="Set up a weekly plan so Taka drafts and publishes for you"
                >
                  <Sparkles size={14} />
                  <span className="hidden sm:inline">Set up my weekly plan</span>
                </Button>
              ) : (
                <DefaultsPopover
                  open={defaultsOpen}
                  onOpenChange={handleDefaultsOpenChange}
                  onEdit={handleOpenEditPlan}
                >
                  <Button
                    variant="t3-tertiary"
                    size="t3-sm"
                    title={isPlanPaused ? 'Autopilot paused, open to resume' : 'Autopilot plan'}
                    aria-label={isPlanPaused ? 'Autopilot plan (paused)' : 'Autopilot plan'}
                    data-testid="plan-settings-trigger"
                  >
                    <span className="relative inline-flex">
                      <Settings2 size={14} />
                      {isPlanPaused && (
                        <span
                          className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full bg-[var(--warning)] ring-2 ring-[var(--bg-2)]"
                          aria-hidden
                        />
                      )}
                    </span>
                    <span className="hidden sm:inline">
                      Autopilot plan
                      {isPlanPaused && <span className="text-[var(--warning)]"> · Paused</span>}
                    </span>
                  </Button>
                </DefaultsPopover>
              )}
              {onCollapse && (
                <Button
                  variant="t3-tertiary"
                  size="t3-sm"
                  onClick={onCollapse}
                  aria-label="Collapse week"
                  aria-expanded="true"
                  title="Collapse"
                >
                  <ChevronUp size={14} />
                </Button>
              )}
            </div>
          </header>

          {isMobile ? (
            <>
              <WeekStrip
                days={days}
                items={items}
                selectedDate={selectedDate}
                onSelect={handleSelectDay}
              />

              <DayDetail
                date={selectedDate}
                dayOffset={offset}
                items={selectedItems}
                optimisticDraftCount={selectedDate ? (optimisticDrafts[selectedDate] ?? 0) : 0}
                expectedCount={selectedExpectedCount}
                pendingActions={pendingActions}
                openingId={openingId}
                onDelete={handleDelete}
                onOpenPost={handleOpenPost}
                onEdit={handleTweak}
                onUnschedule={handleUnschedule}
                onApprove={handleApprovePost}
              />
            </>
          ) : (
            <>
              <WeekGrid
                days={days}
                items={items}
                planChannels={planChannels}
                justGeneratedIds={justGeneratedIds}
                optimisticDrafts={optimisticDrafts}
                openingId={openingId}
                onOpenPost={handleOpenPost}
                onEdit={handleTweak}
                onUnschedule={handleUnschedule}
                onAddPost={addPostForDay}
                onApprove={handleApprovePost}
                onDelete={handleDelete}
              />
              <div className="mt-4 flex justify-center">
                <Button variant="t3-tertiary" size="t3-sm" onClick={() => navigate('/calendar')}>
                  <Calendar size={14} />
                  View full calendar
                </Button>
              </div>
            </>
          )}
        </div>

        {/* No-plan setup: when posts are scheduled but there's no plan, the
            header CTA opens the same wizard the empty state uses, in a dialog. */}
        {wizardOpen && !activePlan && businessId && (
          <Dialog
            open
            onOpenChange={open => {
              if (!open) setWizardOpen(false)
            }}
          >
            <DialogContent variant="dark" size="md">
              <DialogTitle variant="dark" className="sr-only">
                Set up weekly plan
              </DialogTitle>
              <WeeklyPlanWizard
                businessId={businessId}
                onCancel={() => setWizardOpen(false)}
                onCreated={handlePlanCreated}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Edit plan reopens the same stepper as onboarding, pre-filled, in
            edit mode. Quick pause/cancel stays in the manage popover. */}
        {editPlanOpen && activePlan && businessId && (
          <Dialog
            open
            onOpenChange={open => {
              if (!open) setEditPlanOpen(false)
            }}
          >
            <DialogContent variant="dark" size="md">
              <DialogTitle variant="dark" className="sr-only">
                Edit weekly plan
              </DialogTitle>
              <WeeklyPlanWizard
                businessId={businessId}
                existingPlan={activePlan}
                onCancel={() => setEditPlanOpen(false)}
                onCreated={handleEditPlanDone}
              />
            </DialogContent>
          </Dialog>
        )}

        {connectModalState && businessId && (
          <ConnectPlatformsModal
            open
            onOpenChange={handleConnectModalOpenChange}
            platforms={connectModalState.platforms}
            businessId={businessId}
            trigger={connectModalState.trigger}
            creativeId={connectModalState.creativeId}
            onConnected={handleConnectModalConnected}
          />
        )}
      </section>

      {/* The schedule / approve / view-live hub. Opened by clicking any card
          (desktop grid or mobile day strip). */}
      <PostModal
        idea={activeIdea}
        onClose={() => setActiveIdea(null)}
        openedFrom="cockpit"
        entrySource="cockpit"
      />
    </div>
  )
}
